import React, { useState, useCallback } from 'react';
import { Container, Collapse, IconButton, Box, Typography, Badge, Alert, Snackbar, Fab, useTheme, useMediaQuery, ToggleButtonGroup, ToggleButton, Tooltip, BottomNavigation, BottomNavigationAction, Paper } from '@mui/material';
import { ExpandMore, ExpandLess, Add, ViewList, CalendarMonth, Today, Schedule } from '@mui/icons-material';
import { DragDropContext, DropResult } from '@hello-pangea/dnd';
import { User } from 'firebase/auth';
import { Todo } from '../types/todo';
import UserHeader from './todo/UserHeader';
import AddTodoForm from './todo/AddTodoForm';

import NestedTodoSection from './todo/NestedTodoSection';
import CompletedTodosSection from './todo/CompletedTodosSection';
import TaskFormDrawer from './todo/TaskFormDrawer';
import TodoItem from './todo/TodoItem';
import PostponedCalendarView from './todo/PostponedCalendarView';
import PWAInstallPrompt from './PWAInstallPrompt';

// Custom hooks and utilities
import { useTodos } from '../hooks/useTodos';
import { useTodoOperations } from '../hooks/useTodoOperations';
import { useBadgeManager } from '../hooks/useBadgeManager';
import { useViewModeSetting, ViewMode } from '../hooks/useViewModeSetting';
import { ref, remove } from 'firebase/database';
import { database } from '../firebase/config';
import { organizeTaskHierarchy, groupPostponedTodosByDate } from '../utils/todoUtils';
import { formatDateGroupTitle } from '../utils/dateUtils';
import { extractHashtagsFromTexts } from '../utils/linkUtils';

interface TodoListProps {
  user: User;
}

const TodoList: React.FC<TodoListProps> = ({ user }) => {
  const [selectedTodo, setSelectedTodo] = useState<Todo | null>(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState<boolean>(false);
  const [isPostponedExpanded, setIsPostponedExpanded] = useState<boolean>(false);
  const [isAddDrawerOpen, setIsAddDrawerOpen] = useState<boolean>(false);
  const [dragFromCategory, setDragFromCategory] = useState<'today' | 'backlog' | 'postponed' | null>(null);
  const [mobileTab, setMobileTab] = useState<'today' | 'postponed'>('today');

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Custom hooks for state management and operations
  const {
    todos,
    isConnected,
    isOnline,
    hasPendingSync,
    syncNow,
    animateTaskTransition,
    animatingTaskIds,
    movedTasksNotification,
    setMovedTasksNotification,
    showBlockedTasksMovedNotification,
    newTaskIds,
    completingTaskIds,
    uncompletingTaskIds,
    addNewTaskId,
    addCompletingTaskId,
    addUncompletingTaskId,
  } = useTodos(user);

  const { addTodo, deleteTodo, handleSaveEdit, toggleTodo, handleDragEnd } = useTodoOperations({
    user,
    todos,
    animateTaskTransition,
    showBlockedTasksMovedNotification,
    addNewTaskId,
    addCompletingTaskId,
    addUncompletingTaskId,
  });

  // Badge management for PWA icon and browser tab
  const { todayTodoCount, badgeSupported, isAndroidDevice } = useBadgeManager({ todos, isConnected });

  // Today view mode preference (list of tasks vs. calendar of items to deliver)
  const { viewMode: todayViewMode, setViewMode: setTodayViewMode } = useViewModeSetting(user, 'todayViewMode');

  // Mobile Postponed tab view mode (calendar of deliverables by default vs. the
  // postponed list). Distinct key so it defaults to calendar regardless of any
  // legacy `postponedViewMode` value.
  const { viewMode: postponedViewMode, setViewMode: setPostponedViewMode } = useViewModeSetting(
    user,
    'postponedMobileViewMode',
    'calendar'
  );

  // Clean up old eisenhower filter setting from Firebase
  React.useEffect(() => {
    if (user?.uid) {
      const filterRef = ref(database, `users/${user.uid}/settings/eisenhowerFilter`);
      remove(filterRef).catch(() => {});
    }
  }, [user?.uid]);

  const handleTodayViewModeChange = (_event: React.MouseEvent<HTMLElement>, newMode: ViewMode | null) => {
    if (newMode !== null) {
      setTodayViewMode(newMode);
    }
  };

  const handlePostponedViewModeChange = (_event: React.MouseEvent<HTMLElement>, newMode: ViewMode | null) => {
    if (newMode !== null) {
      setPostponedViewMode(newMode);
    }
  };

  console.log('IIJ todayTodoCount', todayTodoCount);

  const handleTodoClick = (todo: Todo) => {
    setSelectedTodo(todo);
    setIsDrawerOpen(true);
  };

  const handleCloseDrawer = () => {
    setIsDrawerOpen(false);
    setSelectedTodo(null);
  };

  const handleSaveEditWrapper = async (field: 'text' | 'description' | 'dueDate' | 'blockedBy' | 'eisenhowerTag', value: string) => {
    if (!selectedTodo) return;
    await handleSaveEdit(selectedTodo, field, value);
  };

  const handleDeleteWrapper = async (todoId: string) => {
    await deleteTodo(todoId);
    setIsDrawerOpen(false);
    setSelectedTodo(null);
  };

  // Helper function to render nested tasks in postponed section using the same logic as NestedTodoSection
  const renderNestedTasksInPostponed = (parentTasksList: Todo[]) => {
    const { hierarchies, standalone } = organizeTaskHierarchy(todos, 'postponed');
    
    // Create unified list and sort by order (same as NestedTodoSection)
    const allParentTasks = [...standalone, ...hierarchies.map(h => h.parent)];
    const filteredTasks = allParentTasks.filter(task => 
      parentTasksList.some(parentTask => parentTask.id === task.id)
    );
    
    filteredTasks.sort((a, b) => {
      if (a.order === b.order) {
        return b.timestamp - a.timestamp;
      }
      return a.order - b.order;
    });

    const renderTaskWithChildren = (todo: Todo, index: number) => {
      // Find if this todo has children
      const hierarchy = hierarchies.find(h => h.parent.id === todo.id);
      const children = hierarchy ? hierarchy.children : undefined;
      
      return (
        <React.Fragment key={todo.stableKey || todo.id}>
          {/* Parent Task */}
          <Box sx={{ mb: 0.5 }}>
            <TodoItem
              todo={todo}
              index={index}
              onToggle={toggleTodo}
              onClick={handleTodoClick}
              isDraggable={false}
              hideDueDate={true}
              isAnimating={animatingTaskIds.has(todo.id)}
              isNewTask={newTaskIds.has(todo.stableKey || todo.id)}
              isCompletingTask={completingTaskIds.has(todo.id)}
              isUncompletingTask={uncompletingTaskIds.has(todo.id)}
              showEisenhowerTag={true}
            />
          </Box>
          
          {/* Nested Children */}
          {children && children.length > 0 && (
            <Box sx={{ ml: 3, mt: -0.5, mb: 0.5, position: 'relative' }}>
              {/* Visual connector line */}
              <Box 
                sx={{ 
                  position: 'absolute',
                  left: -12,
                  top: 0,
                  bottom: 0,
                  width: 2,
                  backgroundColor: 'divider',
                  opacity: 0.5
                }} 
              />
              
              {children.map((child, childIndex) => (
                <Box key={child.stableKey || child.id} sx={{ mb: 0.5, position: 'relative' }}>
                  {/* Horizontal connector line */}
                  <Box 
                    sx={{ 
                      position: 'absolute',
                      left: -12,
                      top: '50%',
                      width: 12,
                      height: 2,
                      backgroundColor: 'divider',
                      opacity: 0.5,
                      transform: 'translateY(-50%)'
                    }} 
                  />
                  
                  <TodoItem
                    todo={child}
                    index={childIndex}
                    onToggle={toggleTodo}
                    onClick={handleTodoClick}
                    isDraggable={false}
                    hideDueDate={true}
                    isAnimating={animatingTaskIds.has(child.id)}
                    isNewTask={newTaskIds.has(child.stableKey || child.id)}
                    isCompletingTask={completingTaskIds.has(child.id)}
                    isUncompletingTask={uncompletingTaskIds.has(child.id)}
                    showEisenhowerTag={true}
                  />
                </Box>
              ))}
            </Box>
          )}
        </React.Fragment>
      );
    };

    return filteredTasks.map((todo, index) => renderTaskWithChildren(todo, index));
  };

  const handleDragStart = useCallback((result: any) => {
    const sourceCategory = result.source.droppableId as 'today' | 'backlog' | 'postponed';
    setDragFromCategory(sourceCategory);
    // Lock body scroll on mobile to prevent page scrolling during drag
    document.body.style.overflow = 'hidden';
    document.body.style.touchAction = 'none';
  }, []);

  const handleDragEndWrapper = async (result: DropResult) => {
    // Unlock body scroll
    document.body.style.overflow = '';
    document.body.style.touchAction = '';
    // Clear drag state
    setDragFromCategory(null);
    
    if (!result.destination) return;

    const sourceDroppableId = result.source.droppableId;
    const destinationDroppableId = result.destination.droppableId;
    const sourceIndex = result.source.index;
    const destinationIndex = result.destination.index;

    if (sourceDroppableId === destinationDroppableId && sourceIndex === destinationIndex) return;

    await handleDragEnd(
      result.draggableId,
      destinationDroppableId as 'today' | 'backlog' | 'postponed',
      destinationIndex
    );
  };

  // Extract all unique hashtags from all todos
  const allHashtags = extractHashtagsFromTexts(todos.map(t => t.text));

  // Filter todos by category
  const todayTodos = todos.filter(todo => !todo.completed && todo.category === 'today');
  const postponedTodos = todos.filter(todo => !todo.completed && todo.category === 'postponed');
  const backlogTodos = todos.filter(todo => !todo.completed && todo.category === 'backlog');
  const completedTodos = todos.filter(todo => todo.completed);

  // Items that need to be delivered: all incomplete, due-dated, non-blocked tasks
  // (today/overdue + postponed). Shown in the Today section's calendar view.
  const deliveryTodos = todos.filter(todo => !todo.completed && !!todo.dueDate && !todo.blockedBy);

  // Group postponed todos by date (only parent tasks, nesting will be handled in rendering)
  const postponedGroups = groupPostponedTodosByDate(postponedTodos, formatDateGroupTitle);

  // Helper to render postponed list content (reused in both desktop and mobile views)
  const renderPostponedListContent = () => (
    <Box sx={{ mt: 1, pl: isMobile ? 0 : 2 }}>
      {postponedGroups.map((group, groupIndex) => {
        // Calculate days until this date
        let daysLabel: string | null = null;
        let diffDays = 0;
        if (group.date && group.date !== 'No Date') {
          const today = new Date();
          today.setHours(0, 0, 0, 0);
          const targetDate = new Date(group.date);
          targetDate.setHours(0, 0, 0, 0);
          diffDays = Math.round((targetDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
          if (diffDays >= 1 && diffDays <= 3) {
            daysLabel = `in ${diffDays}d`;
          } else if (diffDays >= 4 && diffDays <= 6) {
            daysLabel = 'in <1w';
          } else if (diffDays >= 7) {
            daysLabel = '1w+';
          }
        }
        
        // Get pill color based on days - red/orange/yellow → green → gray
        const getPillStyle = () => {
          if (diffDays === 1) return { bg: '#d32f2f', color: '#ffffff' }; // 1d - red
          if (diffDays === 2) return { bg: '#ff9800', color: '#ffffff' }; // 2d - orange
          if (diffDays === 3) return { bg: '#ffc107', color: '#5d4037' }; // 3d - yellow
          if (diffDays <= 6) return { bg: '#4caf50', color: '#ffffff' }; // 4-6d - green (<1w)
          return { bg: '#e0e0e0', color: '#757575' }; // 7d+ - gray (1w+)
        };
        const pillStyle = getPillStyle();
        
        return (
          <Box key={groupIndex} sx={{ mb: groupIndex < postponedGroups.length - 1 ? 2.5 : 0 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <Typography 
                variant="subtitle1" 
                sx={{ 
                  fontWeight: 600,
                  color: 'text.primary',
                  fontSize: '0.95rem'
                }}
              >
                {group.displayDate}
              </Typography>
              {daysLabel && (
                <Box
                  sx={{
                    px: 0.75,
                    py: 0.125,
                    borderRadius: 1,
                    backgroundColor: pillStyle.bg,
                    fontSize: '0.65rem',
                    fontWeight: 500,
                    color: pillStyle.color,
                  }}
                >
                  {daysLabel}
                </Box>
              )}
            </Box>
            <Box sx={{ '& > *': { mb: 0.5 } }}>
              {renderNestedTasksInPostponed(group.parentTasks)}
            </Box>
          </Box>
        );
      })}
    </Box>
  );

  // List/calendar view toggle, shared by the Today section (desktop) and the
  // mobile Postponed tab.
  const renderViewToggle = (
    value: ViewMode,
    onChange: (event: React.MouseEvent<HTMLElement>, newMode: ViewMode | null) => void
  ) => (
    <ToggleButtonGroup value={value} exclusive onChange={onChange} size="small">
      <ToggleButton value="list" aria-label="list view" sx={{ p: 0.5 }}>
        <Tooltip title="List View">
          <ViewList fontSize="small" />
        </Tooltip>
      </ToggleButton>
      <ToggleButton value="calendar" aria-label="calendar view" sx={{ p: 0.5 }}>
        <Tooltip title="Calendar View">
          <CalendarMonth fontSize="small" />
        </Tooltip>
      </ToggleButton>
    </ToggleButtonGroup>
  );

  // Today section: header with the list/calendar toggle, then either the list of
  // today's tasks (drag & drop) or a calendar of items to be delivered.
  // Must be rendered inside a DragDropContext (the list view is a Droppable).
  const renderTodaySection = (showBadge: boolean) => (
    <>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Typography variant="overline" sx={{ fontWeight: 600 }}>
            Today
          </Typography>
          {showBadge && todayTodoCount > 0 && (
            <Badge
              badgeContent={todayTodoCount}
              color="error"
              sx={{
                ml: 1.5,
                '& .MuiBadge-badge': {
                  fontSize: '0.6rem',
                  height: '16px',
                  minWidth: '16px',
                },
              }}
            />
          )}
        </Box>
        {/* Toggle is desktop-only; on mobile the calendar lives in the Postponed tab */}
        {!isMobile && renderViewToggle(todayViewMode, handleTodayViewModeChange)}
      </Box>

      {isMobile || todayViewMode === 'list' ? (
        <NestedTodoSection
          category="today"
          todos={todayTodos}
          title=""
          onToggleTodo={toggleTodo}
          onTodoClick={handleTodoClick}
          animatingTaskIds={animatingTaskIds}
          newTaskIds={newTaskIds}
          completingTaskIds={completingTaskIds}
          uncompletingTaskIds={uncompletingTaskIds}
          shouldHighlightDrop={dragFromCategory === 'today' || dragFromCategory === 'backlog'}
          showEisenhowerTag={true}
        />
      ) : (
        <PostponedCalendarView
          todos={deliveryTodos}
          onTodoClick={handleTodoClick}
        />
      )}
    </>
  );

  return (
    <Container maxWidth="md" sx={{ py: 2, px: { xs: 2.5, sm: 3, md: 4 }, pb: { xs: 10, sm: 2 } }}>
      <UserHeader
        user={user}
        isConnected={isConnected}
        isOnline={isOnline}
        hasPendingSync={hasPendingSync}
        onSyncNow={syncNow}
      />

      {/* Mobile: Conditional rendering based on selected tab */}
      {isMobile ? (
        <>
          {mobileTab === 'today' ? (
            <>
              {/* Today + Backlog - Main active tasks */}
              <Box sx={{ mb: 3 }}>
                <DragDropContext onDragStart={handleDragStart} onDragEnd={handleDragEndWrapper}>
                  {renderTodaySection(false)}

                  {/* Backlog Section */}
                  <Box sx={{ mb: 0, mt: 2 }}>
                    <Typography variant="overline" gutterBottom sx={{ mb: 1, fontWeight: 600 }}>
                      Backlog
                    </Typography>

                    <NestedTodoSection
                      category="backlog"
                      todos={backlogTodos}
                      title=""
                      onToggleTodo={toggleTodo}
                      onTodoClick={handleTodoClick}
                      animatingTaskIds={animatingTaskIds}
                      newTaskIds={newTaskIds}
                      completingTaskIds={completingTaskIds}
                      uncompletingTaskIds={uncompletingTaskIds}
                      shouldHighlightDrop={dragFromCategory === 'today' || dragFromCategory === 'backlog'}
                      showEisenhowerTag={true}
                    />
                  </Box>
                </DragDropContext>
              </Box>

              {/* Completed Tasks */}
              {completedTodos.length > 0 && (
                <Box sx={{ mb: 3 }}>
                  <CompletedTodosSection
                    completedTodos={completedTodos}
                    onToggleTodo={toggleTodo}
                    onTodoClick={handleTodoClick}
                    animatingTaskIds={animatingTaskIds}
                    newTaskIds={newTaskIds}
                    completingTaskIds={completingTaskIds}
                    uncompletingTaskIds={uncompletingTaskIds}
                    showEisenhowerTag={true}
                  />
                </Box>
              )}
            </>
          ) : (
            /* Mobile Postponed Tab - calendar of deliverables (default) or list */
            <Box sx={{ mb: 3 }}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  py: 0.5,
                  mb: 1,
                }}
              >
                <Typography variant="overline" sx={{ fontWeight: 600 }}>
                  Postponed ({postponedTodos.length})
                </Typography>
                {renderViewToggle(postponedViewMode, handlePostponedViewModeChange)}
              </Box>

              {postponedViewMode === 'calendar' ? (
                <PostponedCalendarView
                  todos={deliveryTodos}
                  onTodoClick={handleTodoClick}
                />
              ) : postponedTodos.length > 0 ? (
                renderPostponedListContent()
              ) : (
                <Box sx={{
                  textAlign: 'center',
                  py: 4,
                  color: 'text.secondary'
                }}>
                  <Schedule sx={{ fontSize: 48, opacity: 0.3, mb: 1 }} />
                  <Typography variant="body2">
                    No postponed tasks
                  </Typography>
                </Box>
              )}
            </Box>
          )}
        </>
      ) : (
        /* Desktop: Show all sections */
        <>
          {/* Today + Backlog - Main active tasks */}
          <Box sx={{ mb: 3 }}>
            <DragDropContext onDragStart={handleDragStart} onDragEnd={handleDragEndWrapper}>
              {renderTodaySection(true)}

              {/* Backlog Section with Add Todo Form */}
              <Box sx={{ mb: 0, mt: 2 }}>
                <Typography variant="overline" gutterBottom sx={{ mb: 1, fontWeight: 600 }}>
                  Backlog
                </Typography>
                
                <AddTodoForm onAddTodo={addTodo} allHashtags={allHashtags} />

                <NestedTodoSection
                  category="backlog"
                  todos={backlogTodos}
                  title=""
                  onToggleTodo={toggleTodo}
                  onTodoClick={handleTodoClick}
                  animatingTaskIds={animatingTaskIds}
                  newTaskIds={newTaskIds}
                  completingTaskIds={completingTaskIds}
                  uncompletingTaskIds={uncompletingTaskIds}
                  shouldHighlightDrop={dragFromCategory === 'today' || dragFromCategory === 'backlog'}
                  showEisenhowerTag={true}
                />
              </Box>
            </DragDropContext>
          </Box>

          {/* Postponed Tasks - Separate section, list view only */}
          {postponedTodos.length > 0 && (
            <Box sx={{ mb: 3 }}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  cursor: 'pointer',
                  py: 0.5,
                  borderRadius: 1,
                  '&:hover': {
                    backgroundColor: 'action.hover',
                  },
                }}
                onClick={() => setIsPostponedExpanded(!isPostponedExpanded)}
              >
                <IconButton size="small">
                  {isPostponedExpanded ? <ExpandLess /> : <ExpandMore />}
                </IconButton>
                <Typography variant="overline" sx={{ mb: 0, fontWeight: 600 }}>
                  Postponed ({postponedTodos.length})
                </Typography>
              </Box>

              <Collapse in={isPostponedExpanded}>
                <Box sx={{ pl: 2 }}>
                  {renderPostponedListContent()}
                </Box>
              </Collapse>
            </Box>
          )}

          {/* Completed Tasks - Less visible */}
          {completedTodos.length > 0 && (
            <Box sx={{ mb: 3 }}>
              <CompletedTodosSection
                completedTodos={completedTodos}
                onToggleTodo={toggleTodo}
                onTodoClick={handleTodoClick}
                animatingTaskIds={animatingTaskIds}
                newTaskIds={newTaskIds}
                completingTaskIds={completingTaskIds}
                uncompletingTaskIds={uncompletingTaskIds}
                showEisenhowerTag={true}
              />
            </Box>
          )}
        </>
      )}

      <TaskFormDrawer
        isOpen={isDrawerOpen}
        mode="edit"
        todo={selectedTodo}
        todos={todos}
        onClose={handleCloseDrawer}
        onSaveEdit={handleSaveEditWrapper}
        onDelete={handleDeleteWrapper}
      />

      <TaskFormDrawer
        isOpen={isAddDrawerOpen}
        mode="add"
        onClose={() => setIsAddDrawerOpen(false)}
        onAddTodo={addTodo}
      />

      {/* Mobile FAB - positioned above bottom navigation */}
      {isMobile && mobileTab === 'today' && (
        <Fab
          color="primary"
          aria-label="add task"
          onClick={() => setIsAddDrawerOpen(true)}
          sx={{
            position: 'fixed',
            bottom: 72,
            right: 16,
            zIndex: theme.zIndex.fab,
          }}
        >
          <Add />
        </Fab>
      )}

      {/* Mobile Bottom Navigation */}
      {isMobile && (
        <Paper 
          sx={{ 
            position: 'fixed', 
            bottom: 0, 
            left: 0, 
            right: 0,
            zIndex: theme.zIndex.appBar,
            borderTop: '1px solid',
            borderColor: 'divider',
          }} 
          elevation={3}
        >
          <BottomNavigation
            value={mobileTab}
            onChange={(_event, newValue) => setMobileTab(newValue)}
            showLabels
            sx={{
              height: 56,
              '& .MuiBottomNavigationAction-root': {
                minWidth: 0,
                py: 1,
              },
              '& .MuiBottomNavigationAction-label': {
                fontSize: '0.75rem',
                '&.Mui-selected': {
                  fontSize: '0.75rem',
                },
              },
            }}
          >
            <BottomNavigationAction 
              label="Today" 
              value="today" 
              icon={
                <Box sx={{ position: 'relative' }}>
                  <Today />
                  {todayTodoCount > 0 && (
                    <Box
                      sx={{
                        position: 'absolute',
                        top: -6,
                        right: -10,
                        minWidth: 18,
                        height: 18,
                        borderRadius: '9px',
                        backgroundColor: 'error.main',
                        color: 'white',
                        fontSize: '0.65rem',
                        fontWeight: 600,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        px: 0.5,
                      }}
                    >
                      {todayTodoCount > 99 ? '99+' : todayTodoCount}
                    </Box>
                  )}
                </Box>
              } 
            />
            <BottomNavigationAction 
              label="Postponed" 
              value="postponed" 
              icon={<Schedule />} 
            />
          </BottomNavigation>
        </Paper>
      )}

      <Snackbar
        open={!!movedTasksNotification}
        autoHideDuration={6000}
        onClose={() => setMovedTasksNotification('')}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={() => setMovedTasksNotification('')} 
          severity="info" 
          variant="filled"
          sx={{ width: '100%' }}
        >
          {movedTasksNotification}
        </Alert>
      </Snackbar>

      <PWAInstallPrompt />
    </Container>
  );
};

export default TodoList; 